/**
 * HomeScreen — layout orchestrator.
 *
 * Responsibilities:
 *   • Render layout shell — sidebar / workspace / mobile tab bar
 *   • Compose hooks into a single cardProps object for ActionCard
 *   • Route between card view and queue view based on activeTab
 *
 * All state and business logic lives in hooks:
 *   useSendFlow   → send lifecycle (staging, progress, result)
 *   useFilePicker → file/folder input, drag-drop
 *   useTransferViewModel → pause/resume/cancel sessions
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { useTransferStore, resolveReceiveLink, TransferCoordinator, TransferError } from '@share/core';
import { getPlatformDestinationProvider } from '@share/platform';
import { classifyLink } from '@share/networking';
import { useTransferViewModel } from '@share/app/viewmodels';
import { useSendFlow, useFilePicker } from '@share/app/hooks';
import { copyToClipboard, readFromClipboard } from '@share/shared/utils/clipboard';
import {
  useTheme,
  useBreakpoint,
  Colors,
  Spacing,
  Radius,
  FontWeight,
  Layout,
  TransferList,
  QRScanner,
  DropzoneOverlay,
  SidebarNav,
  SidebarTab,
  TopNavbar,
  ActionCard,
  Icon,
  SettingsModal,
  DynamicBackground,
  useToast,
} from '@share/ui';

// ─── Constants ────────────────────────────────────────────────────────────────

const NAV_TABS: { tab: SidebarTab; icon: 'send' | 'download' | 'transfers'; label: string }[] = [
  { tab: 'send',    icon: 'send',      label: 'Send' },
  { tab: 'receive', icon: 'download',  label: 'Receive' },
  { tab: 'all',     icon: 'transfers', label: 'All' },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface HomeScreenProps {
  initialJoinCode?: string;
  initialTab?: SidebarTab;
  onNavigateToReceive?: (url: string) => void;
}

export const HomeScreen: React.FC<HomeScreenProps> = ({
  initialJoinCode,
  initialTab,
}) => {
  // ── Core hooks ─────────────────────────────────────────────────────────────
  const { colors, isDark } = useTheme();
  const { isDesktop } = useBreakpoint();
  const { transfers, pauseTransfer, resumeTransfer, cancelTransfer } =
    useTransferViewModel();
  const { showToast } = useToast();

  // ── UI state ───────────────────────────────────────────────────────────────
  const [activeTab,      setActiveTab]      = useState<SidebarTab>(initialTab || (initialJoinCode ? 'receive' : 'send'));
  const [joinCode,       setJoinCode]       = useState(initialJoinCode || '');
  const [isScanning,     setIsScanning]     = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [bgIdx,          setBgIdx]          = useState(0);

  // ── Business logic hooks ───────────────────────────────────────────────────
  const filePicker = useFilePicker({
    onFiles: (files) => sendFlow.addFiles(files),
  });

  const sendFlow = useSendFlow({
    onNeedFiles: filePicker.openFilePicker,
  });

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => setBgIdx(i => (i + 1) % Colors.wallpapers.length), 25_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (initialJoinCode) {
      setJoinCode(initialJoinCode);
      setActiveTab('receive');
    }
  }, [initialJoinCode]);

  // ── Smart resume for receive sessions ──────────────────────────────────────
  const handleResume = async (id: string) => {
    const session = useTransferStore.getState().sessions[id];
    if (
      session?.direction === 'receive' &&
      !session.engineTorrentId &&
      session.magnetUri
    ) {
      try {
        const resolved = await resolveReceiveLink(session.magnetUri);
        const provider = getPlatformDestinationProvider();
        const dest = await provider.defaultDestination('share-download');
        await TransferCoordinator.startReceiveSession(resolved, dest);
        return;
      } catch (err) {
        showToast({ type: 'error', message: 'Failed to resume download session.' });
      }
    }
    resumeTransfer(id);
  };

  // ── Receive handlers ───────────────────────────────────────────────────────
  const handleReceive = async () => {
    const raw = joinCode.trim();
    if (!raw) return;
    const classification = classifyLink(raw);
    if (classification.kind === 'invalid') {
      showToast({ type: 'error', message: classification.reason! });
      return;
    }

    try {
      showToast({ type: 'info', message: 'Resolving download link…' });
      const resolved = await resolveReceiveLink(raw);
      const provider = getPlatformDestinationProvider();
      const dest = await provider.defaultDestination('share-download');

      const result = await TransferCoordinator.startReceiveSession(resolved, dest);

      // Clear join code and switch immediately to list view
      setJoinCode('');
      setActiveTab('all');

      switch (result.status) {
        case 'already_seeding':
          showToast({ type: 'info', message: 'You are already seeding this file.' });
          break;
        case 'already_downloading':
          showToast({ type: 'info', message: 'This file is already downloading in your transfers list.' });
          break;
        case 'already_completed':
          showToast({ type: 'success', message: 'You have already downloaded this file.' });
          break;
        case 'resumed':
          showToast({ type: 'success', message: 'Resumed existing transfer.' });
          break;
        case 'started':
        default:
          showToast({ type: 'success', message: 'Download started in background' });
          break;
      }
    } catch (err) {
      const msg =
        err instanceof TransferError
          ? err.message
          : `Download failed: ${err instanceof Error ? err.message : String(err)}`;
      showToast({ type: 'error', message: msg });
    }
  };

  const handleQRScan = (data: string) => {
    setIsScanning(false);
    if (!data) return;
    const raw = data.trim();
    setJoinCode(raw);
    setActiveTab('receive');
  };

  // ── Composed ActionCard props (single source) ──────────────────────────────
  const cardProps = {
    isSending:         sendFlow.isSending,
    isPreparing:       sendFlow.isPreparing,
    prepareProgress:   sendFlow.prepareProgress,
    shareUrl:          sendFlow.shareUrl,
    shareHash:         sendFlow.shareHash,
    torrentName:       sendFlow.torrentName,
    torrentFile:       sendFlow.torrentFile,
    selectedFiles:     sendFlow.stagedFiles,
    onRemoveFile:      sendFlow.removeFile,
    onAddMoreFiles:    filePicker.openFilePicker,
    onSendFileClick:   activeTab === 'send' ? sendFlow.handleTransfer : filePicker.openFilePicker,
    onSendFolderClick: filePicker.openFolderPicker,
    joinCode,
    onJoinCodeChange:  setJoinCode,
    onStartDownload:   handleReceive,
    onOpenQRScanner:   () => setIsScanning(true),
    onPasteLink:       async () => {
      const text = await readFromClipboard();
      if (text) setJoinCode(text.trim());
    },
    onTabChange:       (tab: string) => setActiveTab(tab as SidebarTab),
    onCopy:            copyToClipboard,
    onCloseShare:      () => { sendFlow.handleReset(); },
    onViewDetails:     () => setActiveTab('all'),
  };

  // ── Layout helpers ─────────────────────────────────────────────────────────
  const bgStyle = typeof window !== 'undefined'
    ? { background: Colors.wallpapers[bgIdx], transition: 'background 2s ease-in-out' } as any
    : { backgroundColor: colors.bg };

  const isCardView  = activeTab === 'send' || activeTab === 'receive';
  const isQueueView = activeTab === 'all';

  // ── Overlays ───────────────────────────────────────────────────────────────
  const overlays = (
    <>
      {filePicker.inputs}
      {isScanning && (
        <QRScanner onScanSuccess={handleQRScan} onScanCancel={() => setIsScanning(false)} />
      )}
      <SettingsModal visible={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />
    </>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  const transferListProps = {
    sessions:        transfers,
    filter:          'all' as const,
    onPause:         pauseTransfer,
    onResume:        handleResume,
    onCancel:        (id: string, deleteData?: boolean) => { void cancelTransfer(id, deleteData ?? false); },
    onAddTorrent:    filePicker.openFilePicker,
    onAddFolder:     filePicker.openFolderPicker,
    onReceiveLink:   () => setActiveTab('receive'),
  };

  const root = (
    <View style={[styles.shell, bgStyle]}>
      {isDesktop && <DynamicBackground />}
      {overlays}

      <View style={styles.frame}>
        {isDesktop && (
          <SidebarNav
            activeTab={activeTab}
            onSelectTab={setActiveTab}
            onOpenSettings={() => setIsSettingsOpen(true)}
          />
        )}

        <View style={[
          styles.workspace,
          isDesktop && {
            paddingLeft:   isCardView ? Layout.sidebarWidth + 48 : Layout.sidebarWidth + 24,
            paddingRight:  isCardView ? 40 : 24,
            paddingTop:    isCardView ? 80 : 100,
            paddingBottom: isCardView ? 40 : 24,
          },
        ]}>
          <TopNavbar onNavigateToSettings={() => setIsSettingsOpen(true)} />

          {isDesktop ? (
            <View style={[
              styles.desktopContent,
              isCardView && styles.desktopContentCardMode,
            ]}>
              {isCardView  && <View style={styles.cardPanel}><ActionCard activeTab={activeTab} {...cardProps} /></View>}
              {isQueueView && <View style={styles.queuePanel}><TransferList {...transferListProps} /></View>}
            </View>
          ) : (
            <>
              <ScrollView
                style={styles.mobileScroll}
                contentContainerStyle={[styles.mobileScrollContent, isCardView && styles.mobileScrollCentered]}
                showsVerticalScrollIndicator={false}
              >
                {isCardView  && <ActionCard activeTab={activeTab} {...cardProps} />}
                {isQueueView && <TransferList {...transferListProps} />}
              </ScrollView>

              {/* Mobile bottom tab bar — zero shadows, clean 1px border */}
              <View style={[styles.mobileTabBar, { backgroundColor: colors.surface, borderTopColor: colors.border }]}>
                {NAV_TABS.map(({ tab, icon, label }) => {
                  const active = tab === activeTab;
                  const activeBg = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)';
                  return (
                    <TouchableOpacity key={tab} style={styles.mobileTab} onPress={() => setActiveTab(tab)} activeOpacity={0.75}>
                      <View style={[styles.mobileIconWrap, active && { backgroundColor: activeBg }]}>
                        <Icon name={icon} size={22} color={active ? colors.brand : colors.subtext} />
                      </View>
                      <Text style={[styles.mobileTabLabel, { color: active ? colors.brand : colors.subtext, fontWeight: active ? '700' : '500', marginTop: 2 }]}>
                        {label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
                <TouchableOpacity style={styles.mobileTab} onPress={() => setIsSettingsOpen(true)} activeOpacity={0.75}>
                  <View style={styles.mobileIconWrap}>
                    <Icon name="settings" size={22} color={colors.subtext} />
                  </View>
                  <Text style={[styles.mobileTabLabel, { color: colors.subtext, marginTop: 2 }]}>Settings</Text>
                </TouchableOpacity>
              </View>
            </>
          )}
        </View>
      </View>
    </View>
  );

  return typeof window !== 'undefined' ? (
    <DropzoneOverlay onDrop={filePicker.handleDrop}>{root}</DropzoneOverlay>
  ) : root;
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    width: '100%',
    minHeight: '100vh',
    height: '100vh',
    overflow: 'hidden',
  } as any,
  frame: {
    flex: 1,
    flexDirection: 'row',
    height: '100%',
  },
  workspace: {
    flex: 1,
    height: '100%',
  },
  desktopContent: { 
    flex: 1, 
    justifyContent: 'center',
    maxWidth: Layout.maxContentWidth,
    width: '100%',
    alignSelf: 'center',
  },
  desktopContentCardMode: {
    alignItems: 'flex-start',
  },
  cardPanel: {
    width: Layout.cardWidth,
  },
  queuePanel: {
    flex: 1,
    width: '100%',
  },
  mobileScroll: {
    flex: 1,
  },
  mobileScrollContent: {
    paddingHorizontal: Spacing.md,
    paddingTop: 100,
    paddingBottom: 96,
  },
  mobileScrollCentered: {
    alignItems: 'center',
  },
  mobileTabBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    flexDirection: 'row',
    borderTopWidth: 1,
    paddingTop: 8,
    paddingBottom: 16,
  } as any,
  mobileTab: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileIconWrap: {
    width: 36,
    height: 36,
    borderRadius: Radius.lg,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileTabLabel: {
    fontSize: 11,
    fontWeight: FontWeight.medium,
  },
});
